import type { CdnInfo, LandingPageUser, LandingPageUserTariff, MemberAreaUser } from '@common/types/user.type';
import type { UserState } from './user.reducer';
import { USER_FEATURE_KEY } from './user.reducer';

import { createFeatureSelector, createSelector } from '@ngrx/store';
import { type ETariff, ETariffAbbr, type Widgets } from '@common/enums';
import { selectIsAdmin } from '@web/store/app/app.selectors';
import { environment } from '@web-env/environment';
import { EEnvTypes } from '@common/enums/env-types.enum';
import { checkIsElementAvailable, getCurrentTariffSet } from '@web/helpers/tariffs';
import { tariffsCssClasses } from '@web/core/constants/tariff.const';

const devMode = environment.type === EEnvTypes.DEV;

export const selectUserState = createFeatureSelector<UserState>(USER_FEATURE_KEY);

export const selectUser = createSelector(selectUserState, (state: UserState): LandingPageUser => state?.user);

export const selectUserTariff = createSelector(
    selectUser,
    (user: LandingPageUser): LandingPageUserTariff => user?.tariff
);

export const selectTariffVersion = createSelector(
    selectUserTariff,
    (tariff: LandingPageUserTariff): ETariff => tariff?.version
);

export const selectTariffAbbr = createSelector(
    selectUserTariff,
    (tariff: LandingPageUserTariff): ETariffAbbr => tariff?.abbr
);

export const selectIsUserTariffFree = createSelector(
    selectUserTariff,
    (tariff: LandingPageUserTariff): boolean => tariff?.is_free === 1
);

export const selectIsOffice = createSelector(
    selectUser,
    (user: LandingPageUser): boolean => devMode || !!user?.is_office
);

export const selectUserAvailableSiteCount = createSelector(
    selectUser,
    selectTariffAbbr,
    (user: LandingPageUser, tariffAbbr: ETariffAbbr): number => {
        if (tariffAbbr === ETariffAbbr.PRO_2023 && typeof user?.available_site_count !== 'number') {
            return user.available_site_count.lp;
        }
        if (tariffAbbr === ETariffAbbr.STANDARD_2023) {
            return 0;
        }

        return user?.available_site_count as number;
    }
);

export const selectUserId = createSelector(selectUserState, (state: UserState): number => state?.user?.user_id);

export const selectEduUserTariff = createSelector(
    selectUserState,
    (state: UserState): string => state?.eduUser?.tariff?.abbr
);

export const hasAccessToElement = (widgetName: Widgets) =>
    createSelector(
        selectTariffAbbr,
        selectIsAdmin,
        (tariffAbbr: ETariffAbbr, isAdmin: boolean): { hasAccess: boolean; nextTariff: ETariffAbbr | null } => {
            const fullAccess = isAdmin;
            const { hasAccess, nextTariff } = checkIsElementAvailable(widgetName, tariffAbbr);

            return { hasAccess: fullAccess || hasAccess, nextTariff };
        }
    );

export const selectNextTariff = createSelector(selectTariffAbbr, (tariffAbbr: ETariffAbbr): ETariffAbbr => {
    const tariffsArray = Object.keys(getCurrentTariffSet(tariffAbbr)) as ETariffAbbr[];
    const currentIndex = tariffsArray.indexOf(tariffAbbr);

    return tariffsArray[currentIndex + 1] ?? tariffAbbr;
});

export const selectNextTariffClass = createSelector(
    selectNextTariff,
    (tariffAbbr: ETariffAbbr): string => tariffsCssClasses[tariffAbbr] ?? ''
);

export const selectHasAccessToTab = createSelector(
    selectIsUserTariffFree,
    selectIsAdmin,
    selectNextTariff,
    (
        isFree: boolean,
        isAdmin: boolean,
        nextTariff: ETariffAbbr
    ): { hasAccess: boolean; nextTariff: ETariffAbbr | null } => {
        const hasAccess = isAdmin || !isFree;

        return { hasAccess, nextTariff: hasAccess ? null : nextTariff };
    }
);

export const selectCdnInfo = createSelector(selectUserState, (state: UserState): CdnInfo => state?.cdnInfo);

export const selectMemberAreaUser = createSelector(
    selectUserState,
    (state: UserState): MemberAreaUser => state?.memberAreaUser
);

export const selectMemberAreaUserTimezone = createSelector(
    selectMemberAreaUser,
    (memberAreaUser: MemberAreaUser): string => memberAreaUser?.timezone
);

export const selectMemberAreaUserCreatedAt = createSelector(
    selectMemberAreaUser,
    (memberAreaUser: MemberAreaUser): string => memberAreaUser?.createdAt
);
